import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import Button from "@shared/v2/Button";
import Modal from "@shared/v2/Modal";
import Typography from "@shared/v2/Typography";
import {
  ChevronDownSolidV6,
  DownloadSolidV6,
  LinkSolidV6,
  PencilSolidV6,
  SpinnerSolidV6,
} from "@shared/v2/Icomoon";
import { Popover } from "@shared/v2/Popover";
import useSignInSheet from "./hooks/useSignInSheet";
import { setSnackbar } from "../../../reducers/snackbarReducer";

const getOpenHouseUserMeta = (selectedHosts, openHouse) =>
  selectedHosts.find((host) => host.value === openHouse.hostPersonUuid)?.meta;

const SignInSheetDropdown = ({ className, openHouse, selectedHosts }) => {
  const dispatch = useDispatch();
  const userMeta = useMemo(() => getOpenHouseUserMeta(selectedHosts, openHouse), [selectedHosts, openHouse]);
  const { loading, getSignInSheet, getOpenHouseUrl } = useSignInSheet(openHouse, userMeta);

  return (
    <div className={`tw-flex tw-gap-[12px] ${className}`}>
      <Popover placement="bottom-end">
        <Popover.Trigger>
          <Button schema="secondary" onClick={(e) => e.stopPropagation()}>
            Get Sign-in Sheet <ChevronDownSolidV6 size="s" />
          </Button>
        </Popover.Trigger>
        <Popover.Content>
          <Popover.Item
            icon={<DownloadSolidV6 />}
            onClick={(e) => {
              e.stopPropagation();
              getSignInSheet("pdf").then(({ downloadPdf }) => window.open(downloadPdf, "_blank"));
            }}
          >
            Download Now
          </Popover.Item>
          <Popover.Item
            icon={<PencilSolidV6 />}
            onClick={(e) => {
              e.stopPropagation();
              getSignInSheet("link").then(({ editUrl }) => window.open(editUrl, "_blank"));
            }}
          >
            Customize
          </Popover.Item>
        </Popover.Content>
      </Popover>

      <Button
        schema="secondary"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(getOpenHouseUrl()).then(() => {
            dispatch(
              setSnackbar({
                position: "top",
                show: true,
                text: "Link Copied",
                schema: "executed",
                timeout: 3000,
                closeOnTimeout: () => dispatch(setSnackbar({ show: false })),
              }),
            );
          });
        }}
      >
        <LinkSolidV6 size="s" /> Copy Link
      </Button>

      <Modal
        show={Boolean(loading) && loading !== "link"}
        containerClassName="!tw-z-3000"
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentClassName="tw-max-w-[440px] tw-max-h-[90vh] tw-w-full"
      >
        <Modal.Body className="tw-flex tw-flex-col tw-items-center tw-gap-[24px]">
          <Typography variant="headline1">Creating Sign-In Sheet</Typography>
          <SpinnerSolidV6 size="xl" className="tw-text-theme-primary tw-animate-spin" />
          <Typography variant="body-m" className="!tw-text-gray-50">
            {loading === "pdf"
              ? "Just a moment - your download is starting soon"
              : "We'll send you to Brivity Marketer for the final touches"}
          </Typography>
        </Modal.Body>
      </Modal>
    </div>
  );
};

SignInSheetDropdown.propTypes = {
  className: PropTypes.string,
  openHouse: PropTypes.shape().isRequired,
  selectedHosts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

SignInSheetDropdown.defaultProps = {
  className: "",
};

export default SignInSheetDropdown;
